import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { estoqueApi } from '../../services/api';
import { Material } from '../../types';
import { CubeIcon, PlusIcon, PencilIcon, TrashIcon, ExclamationTriangleIcon, DocumentArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline';

const MaterialList: React.FC = () => {
  const navigate = useNavigate();
  const [materiais, setMateriais] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState<string>('');
  const [uploadError, setUploadError] = useState<string>('');

  useEffect(() => {
    loadMateriais();
  }, []);

  const loadMateriais = async () => {
    try {
      setLoading(true);
      const response = await estoqueApi.getMateriais();
      setMateriais(response.results || []);
    } catch (err) {
      setError('Erro ao carregar materiais');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number, codigo: string) => {
    if (!window.confirm(`Tem certeza que deseja desativar o material "${codigo}"?`)) {
      return;
    }

    try {
      await estoqueApi.deleteMaterial(id);
      loadMateriais();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Erro ao desativar material');
    }
  };

  const handleUploadXML = async (file: File) => {
    setUploading(true);
    setUploadError('');
    setUploadSuccess('');

    try {
      const reader = new FileReader();

      reader.onload = async (e) => {
        const xmlContent = e.target?.result as string;

        try {
          const API_BASE_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:8000`;
          const token = localStorage.getItem('access_token');

          const response = await fetch(`${API_BASE_URL}/api/v1/estoque/notas-fiscais/importar_xml/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ xml_content: xmlContent })
          });

          if (!response.ok) {
            const errorData = await response.json();
            const errorMsg = errorData.error || 'Erro ao importar NFe';

            // Verificar se é erro de duplicata
            if (errorMsg.includes('já importada')) {
              // Não é um erro real, apenas informação
              setUploadSuccess(`ℹ️ ${errorMsg}`);
              setTimeout(() => {
                navigate('/estoque/notas-fiscais');
              }, 3000);
            } else {
              // Erro real
              throw new Error(errorMsg);
            }
            return;
          }

          const data = await response.json();
          setUploadSuccess(`✓ NFe ${data.numero_nota}/${data.serie} importada com sucesso! ${data.itens?.length || 0} itens encontrados e ${data.itens_mapeados || 0} materiais criados/mapeados.`);

          // Redirecionar para a página de notas fiscais após 2 segundos
          setTimeout(() => {
            navigate('/estoque/notas-fiscais');
          }, 2000);

        } catch (err: any) {
          setUploadError(err.message || 'Erro ao processar XML');
        } finally {
          setUploading(false);
        }
      };

      reader.readAsText(file);
    } catch (err: any) {
      setUploadError('Erro ao ler arquivo XML');
      setUploading(false);
    }
  };

  const filteredMateriais = materiais.filter(material =>
    material.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    material.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    material.categoria_display?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getStatusBadge = (material: Material) => {
    if (material.precisa_reposicao) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
          Baixo
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        ✓ OK
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-base font-bold text-gray-900">Materiais</h1>
          <p className="text-xs text-gray-600">Gerencie o estoque de materiais</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
          >
            <DocumentArrowUpIcon className="h-4 w-4 mr-1.5" />
            Importar NFe XML
          </button>
          <button
            onClick={() => navigate('/estoque/materiais/novo')}
            className="flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4 mr-1.5" />
            Novo Material
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Busca */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <input
          type="text"
          placeholder="Buscar por código, nome ou categoria..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Código
              </th>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nome
              </th>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Categoria
              </th>
              <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Saldo
              </th>
              <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Mín/Máx
              </th>
              <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Preço Médio
              </th>
              <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {filteredMateriais.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-2 py-4 text-center text-sm text-gray-500">
                  Nenhum material encontrado
                </td>
              </tr>
            ) : (
              filteredMateriais.map((material) => (
                <tr key={material.id} className="hover:bg-gray-25">
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <div className="text-xs font-medium text-gray-900">{material.codigo}</div>
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="text-xs text-gray-900">{material.nome}</div>
                    {material.descricao && (
                      <div className="text-xs text-gray-500 truncate max-w-xs">{material.descricao}</div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <div className="text-xs text-gray-900">{material.categoria_display}</div>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-center">
                    <div className="text-xs font-semibold text-gray-900">
                      {material.saldo_atual} {material.unidade_display}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-center">
                    <div className="text-xs text-gray-600">
                      {material.estoque_minimo} / {material.estoque_maximo}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-right">
                    <div className="text-xs text-gray-900">
                      {formatCurrency(material.preco_medio_ponderado)}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-center">
                    {getStatusBadge(material)}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-right">
                    <div className="flex justify-end space-x-1">
                      <button
                        onClick={() => navigate(`/estoque/materiais/editar/${material.id}`)}
                        className="text-blue-600 hover:text-blue-900 p-0.5"
                        title="Editar"
                      >
                        <PencilIcon className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(Number(material.id), material.codigo)}
                        className="text-red-600 hover:text-red-900 p-0.5"
                        title="Desativar"
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="px-4 py-3 border-t border-gray-200 text-xs text-gray-600">
          Total: {filteredMateriais.length} material(is)
        </div>
      </div>

      {/* Modal de Upload XML */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Importar NFe XML</h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadError('');
                  setUploadSuccess('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Selecione o arquivo XML da Nota Fiscal Eletrônica para importar os produtos automaticamente.
              </p>

              {uploadError && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                  {uploadError}
                </div>
              )}

              {uploadSuccess && (
                <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded text-sm">
                  {uploadSuccess}
                </div>
              )}

              <div className="mt-4">
                <label className="block">
                  <input
                    type="file"
                    accept=".xml"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleUploadXML(file);
                      }
                    }}
                    disabled={uploading}
                    className="block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-md file:border-0
                      file:text-sm file:font-semibold
                      file:bg-blue-50 file:text-blue-700
                      hover:file:bg-blue-100
                      disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </label>
              </div>

              {uploading && (
                <div className="mt-4 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-sm text-gray-600">Processando XML...</span>
                </div>
              )}

              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded text-sm">
                <p className="font-semibold text-blue-900 mb-2">Como funciona:</p>
                <ol className="list-decimal list-inside text-blue-800 space-y-1 text-xs">
                  <li>O sistema lê os dados da NFe</li>
                  <li>Produtos são mapeados automaticamente</li>
                  <li>Você poderá conferir e ajustar antes de lançar no estoque</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialList;
